"use client";

import HelpPanel from "@cloudscape-design/components/help-panel";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Box from "@cloudscape-design/components/box";
import Link from "next/link";
import { useHelpPanel, type HelpContent } from "./HelpPanelContext";
import { useAuth } from "@/features/auth/useAuth";
import { CopyButton } from "./CopyButton";
import { OdbcConnectionPanel } from "./OdbcConnectionPanel";
import { BRAND } from "@/lib/theme/palette";

function isInternalUser(role: string | undefined): boolean {
  return role === "super_admin";
}

function HelpBody({ content }: { content: HelpContent }): React.JSX.Element {
  const { user } = useAuth();

  return (
    <>
      <Box variant="p" color="text-body-secondary">
        {content.definition}
      </Box>

      {content.methodology && (
        <ExpandableSection headerText="Methodology" defaultExpanded>
          <Box variant="p">{content.methodology}</Box>
        </ExpandableSection>
      )}

      {content.dataSources && (
        <ExpandableSection headerText="Data Sources">
          <Box variant="p">{content.dataSources}</Box>
        </ExpandableSection>
      )}

      {content.adminNotes && isInternalUser(user?.role) && (
        <ExpandableSection headerText="Internal Notes">
          <div
            style={{
              background: "#f9f5ff",
              padding: "8px 12px",
              borderRadius: 4,
              borderLeft: `3px solid ${BRAND.depth}`,
              fontSize: 13,
              lineHeight: 1.6,
              whiteSpace: "pre-line",
            }}
          >
            {content.adminNotes}
          </div>
        </ExpandableSection>
      )}

      {content.sqlReference && isInternalUser(user?.role) && (
        <ExpandableSection headerText="Query Reference">
          <Box variant="p" color="text-body-secondary">
            View or function used to generate this metric:
          </Box>
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <CopyButton text={content.sqlReference} label="Copy SQL" />
          </div>
          <pre
            style={{
              background: "#f2f3f3",
              padding: "8px 12px",
              borderRadius: 4,
              fontSize: 13,
              overflowX: "auto",
              marginTop: 8,
            }}
          >
            {content.sqlReference}
          </pre>
          <OdbcConnectionPanel />
        </ExpandableSection>
      )}

      {content.fullMethodologySlug && (
        <Box padding={{ top: "m" }}>
          <Link href={`/help/methodology/${content.fullMethodologySlug}`}>
            View full methodology →
          </Link>
        </Box>
      )}
    </>
  );
}

export function HelpPanelWidget(): React.JSX.Element {
  const { content } = useHelpPanel();

  if (!content) {
    return (
      <HelpPanel header={<h2>Help</h2>}>
        <Box variant="p" color="text-body-secondary">
          Select a metric or section to see its methodology and data source
          information.
        </Box>
      </HelpPanel>
    );
  }

  return (
    <HelpPanel header={<h2>{content.title}</h2>}>
      <HelpBody content={content} />
    </HelpPanel>
  );
}
